import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, NumberInput, Table} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import { Route, UpsertRoute } from '@/models/route.model';
import { upsertRoute } from '@/services/route.service';
import { getCitiesLookup, getCity } from '@/services/city.service';
import MapToSelectPath from '@/components/MapToSelectPath';
import { City } from '@/models/city.model';
const { Column, HeaderCell, Cell } = Table;

const schema = Schema.Model<UpsertRoute>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  length: Schema.Types.NumberType().isRequired('This field is required.'),
  path: Schema.Types.ArrayType().isRequired('This field is required.'),
});

const emptyModel: UpsertRoute = {
  id: undefined,
  name: '',
  cityId: undefined,
  length: undefined,
  path: undefined
};

interface RouteFormProps{
  cityId?: number;
  formObject?: Route;
  pageState: PageState
};

const RouteForm: React.FC<RouteFormProps> = ({formObject, cityId, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertRoute>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [city, setCity] = useState<City | undefined>(undefined);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, cityId});
      updateCity(cityId);
      return;
    }

    if (formObject) {
      const castedModel: UpsertRoute = { 
        ...formObject, 
      };

      setFormValue(castedModel);
      updateCity(formObject.cityId);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);


  const updateCity = (cityId?: number) => {
    if(cityId){
      getCity(cityId).then(res => setCity(res.result));
    }
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await upsertRoute(formValue);
      if (result.success) {
        if(cityId){
          nav(`/cities/${formValue.cityId}`, {state:{tab: "routes"}});
        }else{
          nav(`/routes`);
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='upsert-route' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={schema}>
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name"/>
              </Form.Group>
              <Form.Group>
                <Form.Label>City</Form.Label>
                <Form.Control name="cityId" 
                  accepter={FetchingSelect} block
                  fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
                  onChange={val => updateCity(val)}
                  placeholder="Select a city"
                  disabled={!!cityId}
                  />
              </Form.Group>
              <Form.Group>
                <Form.Label>Length (meters)</Form.Label>
                <Form.Control name="length" accepter={NumberInput}/>
              </Form.Group>
              <Form.Group>
                <Form.Label>Path ({`${formValue.path?.length ?? 0} Points`})</Form.Label>
                <Table data={formValue.path} height={500}>      
                  <Column flexGrow={0.5} align="left" >
                    <HeaderCell>#</HeaderCell>
                    <Cell>{(rowData, i) => ((i ?? 0) + 1)}</Cell>
                  </Column>
                  <Column flexGrow={1} align="left" >
                    <HeaderCell>Latitude</HeaderCell>
                    <Cell dataKey="lat" />
                  </Column>
                  <Column flexGrow={1} align="left" >
                    <HeaderCell>Longitude</HeaderCell>
                    <Cell dataKey="lng" />
                  </Column>
                </Table>              
              </Form.Group>              
            </Col>
            <Col span={{xs:24, xl:16}}>
              <Form.Group>
                <Form.Label style={{ textAlign: 'center'}}>Specify route points on the map</Form.Label>
                 <Form.Control accepter={MapToSelectPath} name='path'
                  value={formValue.path}
                  onChange={path => setFormValue(pre => ({...pre, path}))}
                  defaultCenter={city && {lat: city?.lat, lng: city?.lon}}
                  height='750px'
                />                
              </Form.Group>

            </Col>
            {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
          </Row>
        </Grid>
      </Form>
      <Divider />
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default RouteForm;